import { FC, useState, useEffect } from 'react';
import { IconStyle, Tag, TagStyle, Theme, useTranslation } from '@nextgen-web-framework/all';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  type FulfillmentType,
  shippingDetailsDataTransformer,
  FulfillmentTypeIdEnum,
  homeDeliveryDataTransformer,
} from '@commerce-subscription-webcomponents/sdk';
import { PhoneNumberDisplay } from '@commerce-profile-webcomponents/ui';
import { SubscriptionShippingDetailsProps } from './types/propTypes';
import styles from './SubscriptionShippingDetails.module.scss';

const SubscriptionShippingDetails: FC<SubscriptionShippingDetailsProps> = ({
  deliveryOption,
  deliveryType,
  profile,
  displayConfig,
}: SubscriptionShippingDetailsProps) => {
  const t = useTranslation();
  const standardDeliveryText = t('subscription.deliveryoption.standardDeliveryText');
  const freeShippingText = t('subscription.deliveryoption.freeShippingText');
  const deliveryText = t('subscription.CheckoutShippingType.radioLabelDelivery');
  const pickupText = t('subscription.CheckoutShippingType.radioLabelPickup');

  const {
    showAddressTitle = true,
    showSippingContact = true,
    showShippingAddress = true,
    showFulfillmentType = true,
    showFulfillmentHeader = true,
    showPickupInstruction,
    isShippingFree,
    profileCardTitle,
  } = displayConfig;
  const [addressType, setAddressType] = useState<FulfillmentTypeIdEnum | undefined>(deliveryType);
  const [homeAddress, setHomeAddress] = useState<FulfillmentType['homeAddress']>();
  const [pickUpStore, setPickUpStore] = useState<FulfillmentType['storeAddress']>();

  useEffect(() => {
    if (profile?.fulfillment) {
      const ShippingData = shippingDetailsDataTransformer(profile.fulfillment);
      setAddressType(ShippingData?.id);
      setHomeAddress(ShippingData?.homeAddress);
      setPickUpStore(ShippingData?.storeAddress);
    } else if (profile?.deliveryAddress) {
      setAddressType(FulfillmentTypeIdEnum.HOME_DELIVERY);
      setHomeAddress(homeDeliveryDataTransformer(profile?.deliveryAddress));
    }
  }, [profile]);

  const pickUpAddressLine = (storeDetails: FulfillmentType['storeAddress']) => {
    if (storeDetails?.serviceLocation?.address) {
      const { line1, line2, postCode, city, country } = storeDetails.serviceLocation.address;
      return (
        <div className={styles['shipping-data']}>
          {line1}
          {line2 && `, ${line2}`}
          {city && postCode ? `, ${city} ${postCode}` : city ? `, ${city}` : postCode ? `, ${postCode}` : ''}
          {country && `, ${country}`}
        </div>
      );
    } else {
      return <></>;
    }
  };

  // const getCountry = (country: string) =>
  //   country.length == 2 ? t(`profile.contactmasterdata.v1.draft.stateCode.${country}`) : country;

  const PickUpStoreDetails = ({ className }: { className?: string }) => (
    <div className={`${styles['shipping-title']} ${className}`}>
      {pickUpStore?.serviceLocation?.name}
      {pickUpStore?.serviceLocation?.id && (
        <>
          <span className={styles['spearhead']}> • </span>
          {pickUpStore.serviceLocation?.id}
        </>
      )}
    </div>
  );

  const isPickup = addressType === FulfillmentTypeIdEnum.BOPIS;

  const tag = {
    style: TagStyle.OUTLINE,
    text: isPickup ? pickupText : deliveryText,
    theme: Theme.PURPLE,
    icon: {
      iconName: isPickup ? ('location-dot' as IconName) : ('truck' as IconName),
      theme: Theme.PURPLE,
      iconStyle: IconStyle.SOLID,
    },
  };

  return (
    <div className={styles['shipping-details-container']} data-testid="shipping-details-testid">
      {addressType === FulfillmentTypeIdEnum.HOME_DELIVERY && homeAddress && showShippingAddress && (
        <div className={styles['shipping']}>
          <div className={styles['shipping-label']} data-testid="shipping-label-enhanced">
            <Tag {...tag} key={tag.text} />
          </div>
          <div className={styles['address']}>
            {showAddressTitle && (
              <div className={styles['shipping-title']}>{t('subscription.deliveryOption.homeAddress.text')}</div>
            )}
            <div className={styles['shipping-data']} data-testid="delivery-details">
              {showSippingContact && homeAddress?.contact && (
                <>
                  {homeAddress?.contact} <PhoneNumberDisplay phoneNumber={homeAddress.phone} />
                  <br />
                </>
              )}
              {homeAddress?.address && homeAddress.address}
            </div>
            {homeAddress?.deliveryInstructions && (
              <div className={styles['shipping-instruction']}>
                <i>&lsquo;{homeAddress?.deliveryInstructions}&rsquo;</i>
              </div>
            )}
          </div>
        </div>
      )}

      {addressType === FulfillmentTypeIdEnum.BOPIS && pickUpStore && showShippingAddress && (
        <div className={styles['shipping']}>
          <div className={styles['shipping-label']} data-testid="pickup-label-enhanced">
            <Tag {...tag} key={tag.text} />
          </div>
          <div data-testid="pickup-store-details">
            {showSippingContact && !profileCardTitle && <PickUpStoreDetails />}
            {pickUpAddressLine(pickUpStore)}
            {pickUpStore?.serviceLocation?.phone && (
              <div className={styles['shipping-data']}>
                <PhoneNumberDisplay phoneNumber={pickUpStore?.serviceLocation?.phone} />
              </div>
            )}
          </div>
          {showPickupInstruction && (
            <div className={styles['pickup-instruction']}>
              {t('subscription.deliveryOption.pickUpAvailableFrom.text')}
            </div>
          )}
        </div>
      )}

      {showFulfillmentType && (
        <div className={styles['shipping']}>
          <div className={styles['shipping-details']}>
            {showFulfillmentHeader && (
              <div className={styles['shipping-label']}>
                {t('subscription.subscriptionConfirmation.deliveryOptionText')}
              </div>
            )}
            {addressType === FulfillmentTypeIdEnum.HOME_DELIVERY ? (
              <div className={styles['shipping-data']}>
                {standardDeliveryText}{' '}
                {isShippingFree || (deliveryOption?.deliveryCharge && parseInt(deliveryOption.deliveryCharge)) == 0
                  ? ` - ${freeShippingText}`
                  : ''}
              </div>
            ) : (
              <div className={styles['shipping-title']}>{t('subscription.deliveryOption.pickUpAmwayStore.text')}</div>
            )}
            <div className={styles['shipping-data']}>
              {addressType === FulfillmentTypeIdEnum.HOME_DELIVERY
                ? t('subscription.deliveryoption.deliveryETA', {
                    min: deliveryOption?.minimumNumberOfDays ? deliveryOption.minimumNumberOfDays.toString() : '0',
                    max: deliveryOption?.maximumNumberOfDays ? deliveryOption.maximumNumberOfDays.toString() : '0',
                  })
                : t('subscription.deliveryOption.pickUpAvailableFrom.text')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionShippingDetails;
